import React from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Download, Mail, Calendar as CalendarIcon } from 'lucide-react';
import { CalendarDay } from './types';
import { generateICSFile } from '../../../utils/calendar';

interface CalendarExportProps {
  days: CalendarDay[];
  caseName: string;
}

export const CalendarExport: React.FC<CalendarExportProps> = ({
  days,
  caseName,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (format: 'ics' | 'google') => {
    const events = days.flatMap(day => 
      day.events.map(event => ({
        title: event.title,
        date: day.date.toDate(),
        type: event.type,
      }))
    );

    if (format === 'ics') {
      const icsContent = generateICSFile(events, caseName);
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${caseName.replace(/\s+/g, '_')}_calendar.ics`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (format === 'google') {
      // Open Google Calendar in a new tab with pre-filled event details
      const event = events[0]; // For demonstration, using first event
      const googleUrl = new URL('https://calendar.google.com/calendar/render');
      googleUrl.searchParams.append('action', 'TEMPLATE');
      googleUrl.searchParams.append('text', event.title);
      googleUrl.searchParams.append('dates', dayjs(event.date).format('YYYYMMDDTHHmmss') + '/' + dayjs(event.date).add(1, 'hour').format('YYYYMMDDTHHmmss'));
      window.open(googleUrl.toString(), '_blank');
    }

    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Download size={18} />}
        onClick={handleClick}
        size="small"
      >
        Export Calendar
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleExport('ics')}>
          <ListItemIcon>
            <CalendarIcon size={18} />
          </ListItemIcon>
          <ListItemText>Export as ICS</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('google')}>
          <ListItemIcon>
            <Mail size={18} />
          </ListItemIcon>
          <ListItemText>Add to Google Calendar</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};